
import React from 'react';
import { User, UserProgress } from '@/types/user';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LogOut, Award } from 'lucide-react';
import BadgeCard from '@/components/BadgeCard';
import { getBadges, getUnlockedBadges } from '@/utils/gamification';
import AdminStatusToggle from '@/components/AdminStatusToggle';

interface ProfileSidebarProps {
  user: User;
  userProgress: UserProgress;
  logout: () => void;
  setActiveTab: (tab: string) => void;
}

const ProfileSidebar: React.FC<ProfileSidebarProps> = ({ 
  user, 
  userProgress, 
  logout,
  setActiveTab
}) => {
  // First get all badges, then filter for unlocked ones
  const allBadges = getBadges(userProgress);
  const unlockedBadges = getUnlockedBadges(allBadges);
  const recentBadges = unlockedBadges.slice(0, 4); // Get 4 most recent badges
  
  return (
    <div className="w-full md:w-1/3">
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center">
            <Avatar className="h-24 w-24 mb-4">
              <AvatarImage src={user.avatar} alt={user.name} />
              <AvatarFallback className="text-2xl">{user.name.charAt(0)}</AvatarFallback>
            </Avatar>
            <h2 className="text-xl font-semibold">{user.name}</h2>
            <p className="text-muted-foreground">{user.email}</p>
            
            <div className="mt-4 flex gap-2 flex-wrap justify-center">
              <Badge variant="outline" className="bg-hormony-beige/20">
                Day {userProgress.currentDay}
              </Badge>
              <Badge variant="outline" className="bg-hormony-coral/20">
                {userProgress.streak} day streak
              </Badge>
              <Badge variant="outline" className="bg-hormony-beige/20">
                {userProgress.points} points
              </Badge>
            </div>
            
            <Button 
              variant="outline" 
              className="mt-6 w-full"
              onClick={() => logout()}
            >
              <LogOut size={16} className="mr-2" /> Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Admin Status Toggle */}
      {user.isAdmin !== undefined && (
        <Card className="mt-6">
          <CardContent className="pt-6">
            <AdminStatusToggle />
          </CardContent>
        </Card>
      )}
      
      {/* Recent Badges */}
      {recentBadges.length > 0 && (
        <Card className="mt-6">
          <CardContent className="py-4">
            <h3 className="text-sm font-medium flex items-center mb-3">
              <Award size={16} className="mr-1.5 text-hormony-coral" />
              Recent Badges
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {recentBadges.map(badge => (
                <BadgeCard
                  key={badge.id}
                  badge={badge}
                  size="sm"
                  showProgress={false}
                />
              ))}
            </div>
            {unlockedBadges.length > 4 && (
              <div className="mt-3 text-center">
                <Button 
                  variant="link" 
                  size="sm" 
                  className="text-hormony-coral"
                  onClick={() => setActiveTab('badges')}
                >
                  See all ({unlockedBadges.length})
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProfileSidebar;
